import { useEffect, useState } from 'react';
import { Link, useNavigate, Outlet, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, Package, ShoppingBag, MessageSquare, Users, 
  Settings, LogOut, Tag, Image, BarChart3, Bell, Menu, X,
  ChevronDown, Megaphone, ImageIcon, Star, Mail, RotateCcw, RefreshCw,
  Layers, Home, Ticket, Zap, Gift, PartyPopper, Boxes, Globe
} from 'lucide-react';
import { toast } from 'sonner';

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [admin, setAdmin] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    const adminData = localStorage.getItem('admin_user');
    
    if (!token || !adminData) {
      navigate('/admin/login');
      return;
    }
    
    try {
      setAdmin(JSON.parse(adminData));
      setIsAuthenticated(true);
    } catch (e) {
      navigate('/admin/login');
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    toast.success('Logged out successfully');
    navigate('/admin/login');
  };

  const isActive = (path) => location.pathname.startsWith(path);

  const navItems = [
    { type: 'item', path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { type: 'separator', label: 'Products' },
    { type: 'item', path: '/admin/products', icon: Package, label: 'Products' },
    { type: 'item', path: '/admin/categories', icon: Tag, label: 'Categories' },
    { type: 'separator', label: 'Homepage' },
    { type: 'item', path: '/admin/banners', icon: Image, label: 'Hero Banners' },
    { type: 'item', path: '/admin/homepage-sections', icon: Layers, label: 'Sections' },
    { type: 'item', path: '/admin/homepage-reviews', icon: Star, label: 'Reviews (Homepage)' },
    { type: 'item', path: '/admin/newsletter', icon: Mail, label: 'Newsletter' },
    { type: 'separator', label: 'Orders & Customers' },
    { type: 'item', path: '/admin/orders', icon: ShoppingBag, label: 'Orders' },
    { type: 'item', path: '/admin/returns', icon: RotateCcw, label: 'Returns' },
    { type: 'item', path: '/admin/customers', icon: Users, label: 'Customers' },
    { type: 'separator', label: 'Other' },
    { type: 'item', path: '/admin/reviews', icon: MessageSquare, label: 'Product Reviews' },
    { type: 'separator', label: 'Marketing' },
    { type: 'item', path: '/admin/marketing', icon: Megaphone, label: 'Overview' },
    { type: 'item', path: '/admin/coupons', icon: Ticket, label: 'Coupons' },
    { type: 'item', path: '/admin/flash-sales', icon: Zap, label: 'Flash Sales' },
    { type: 'item', path: '/admin/bundle-offers', icon: Gift, label: 'Bundle Offers' },
    { type: 'item', path: '/admin/festival-campaigns', icon: PartyPopper, label: 'Campaigns' },
    { type: 'separator', label: 'Inventory' },
    { type: 'item', path: '/admin/inventory', icon: Boxes, label: 'Stock Management' },
    { type: 'item', path: '/admin/inventory-sync', icon: RefreshCw, label: 'Inventory Sync' },
    { type: 'separator', label: 'System' },
    { type: 'item', path: '/admin/reports', icon: BarChart3, label: 'Reports' },
    { type: 'item', path: '/admin/seo', icon: Globe, label: 'SEO Settings' },
    { type: 'item', path: '/admin/settings', icon: Settings, label: 'Settings' },
  ];

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-ivory flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-beige/30 flex">
      {/* Sidebar - Desktop */}
      <aside 
        className={`fixed left-0 top-0 h-full bg-ivory border-r border-charcoal/10 z-40 transition-all duration-300 hidden lg:block ${
          sidebarOpen ? 'w-64' : 'w-20'
        }`}
      >
        {/* Logo */}
        <div className="h-20 flex items-center justify-between px-6 border-b border-charcoal/10">
          {sidebarOpen ? (
            <Link to="/admin/dashboard" className="block">
              <span className="font-heading text-xl font-light text-charcoal">JAT FASHION</span>
              <span className="block text-[8px] tracking-[0.2em] uppercase text-gold font-medium">Admin Panel</span>
            </Link>
          ) : (
            <Link to="/admin/dashboard" className="block text-center w-full">
              <span className="font-heading text-2xl font-light text-charcoal">J</span>
            </Link>
          )}
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1 overflow-y-auto h-[calc(100vh-200px)]">
          {navItems.map((item, idx) => (
            item.type === 'separator' ? (
              sidebarOpen ? (
                <div key={idx} className="pt-4 pb-2">
                  <p className="text-[10px] tracking-wider uppercase text-charcoal-light/60 font-medium px-4">
                    {item.label}
                  </p>
                </div>
              ) : (
                <div key={idx} className="border-t border-charcoal/5 my-2" />
              )
            ) : (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                  isActive(item.path)
                    ? 'bg-gold text-white font-medium'
                    : 'text-charcoal-light hover:bg-beige hover:text-charcoal'
                } ${!sidebarOpen ? 'justify-center' : ''}`}
                title={item.label}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
                {sidebarOpen && <span className="truncate">{item.label}</span>}
              </Link>
            )
          ))}
        </nav>

        {/* User & Logout */}
        <div className="absolute bottom-0 left-0 right-0 border-t border-charcoal/10 p-4">
          {sidebarOpen ? (
            <div className="mb-3">
              <p className="text-sm font-medium text-charcoal truncate">{admin?.full_name || 'Admin'}</p>
              <p className="text-xs text-charcoal-light truncate">{admin?.email}</p>
            </div>
          ) : null}
          <button
            onClick={handleLogout}
            className={`flex items-center gap-3 w-full px-4 py-3 text-sm text-charcoal-light hover:bg-beige hover:text-charcoal transition-colors ${
              !sidebarOpen ? 'justify-center' : ''
            }`}
          >
            <LogOut className="w-5 h-5" strokeWidth={1.5} />
            {sidebarOpen && <span>Sign Out</span>}
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-ivory border-b border-charcoal/10 z-40 flex items-center justify-between px-4">
        <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2">
          {mobileMenuOpen ? <X className="w-6 h-6 text-charcoal" /> : <Menu className="w-6 h-6 text-charcoal" />}
        </button>
        <span className="font-heading text-lg font-light text-charcoal">JAT ADMIN</span>
        <button onClick={handleLogout} className="p-2">
          <LogOut className="w-5 h-5 text-charcoal-light" />
        </button>
      </div>

      {/* Mobile Sidebar */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-30 bg-charcoal/50" onClick={() => setMobileMenuOpen(false)}>
          <aside 
            className="absolute left-0 top-16 bottom-0 w-72 bg-ivory overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <nav className="p-4 space-y-1">
              {navItems.filter(item => item.type === 'item').map(item => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
                    isActive(item.path)
                      ? 'bg-gold text-white font-medium'
                      : 'text-charcoal-light hover:bg-beige hover:text-charcoal'
                  }`}
                >
                  <item.icon className="w-5 h-5" strokeWidth={1.5} />
                  <span>{item.label}</span>
                </Link>
              ))}
            </nav>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <main className={`flex-1 transition-all duration-300 ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-20'} pt-16 lg:pt-0`}>
        {/* Top Bar */}
        <div className="hidden lg:flex h-20 bg-ivory border-b border-charcoal/10 items-center justify-between px-8">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 hover:bg-beige transition-colors"
          >
            <Menu className="w-5 h-5 text-charcoal" />
          </button>
          
          <div className="flex items-center gap-4">
            <button className="relative p-2 hover:bg-beige transition-colors">
              <Bell className="w-5 h-5 text-charcoal-light" strokeWidth={1.5} />
              <span className="absolute top-1 right-1 w-2 h-2 bg-gold rounded-full" />
            </button>
            <Link to="/" target="_blank" className="text-sm text-charcoal-light hover:text-charcoal">
              View Store →
            </Link>
            <div className="flex items-center gap-3 pl-4 border-l border-charcoal/10">
              <div className="w-9 h-9 bg-gold flex items-center justify-center">
                <span className="text-white font-medium text-sm">
                  {admin?.full_name?.charAt(0) || 'A'}
                </span>
              </div>
              <div>
                <p className="text-sm font-medium text-charcoal">{admin?.full_name || 'Admin'}</p>
                <p className="text-xs text-charcoal-light">Administrator</p>
              </div>
            </div>
          </div>
        </div>

        {/* Page Content */}
        <div className="p-4 md:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
